import { useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/useLanguage";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2 } from "lucide-react";

const ContactForm = () => {
  const { t, i18n } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const { localize } = useLanguage();

  const contactSchema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, { message: t("contactForm.errors.nameRequired") })
          .max(100, { message: t("contactForm.errors.nameMax") }),
        email: z
          .string()
          .trim()
          .email({ message: t("contactForm.errors.emailInvalid") })
          .max(255, { message: t("contactForm.errors.emailMax") }),
        company: z
          .string()
          .trim()
          .max(100, { message: t("contactForm.errors.companyMax") })
          .optional(),
        phone: z
          .string()
          .trim()
          .max(20, { message: t("contactForm.errors.phoneMax") })
          .optional(),
        topic: z
          .string()
          .trim()
          .min(1, { message: t("contactForm.errors.topicRequired") })
          .max(200, { message: t("contactForm.errors.topicMax") }),
        message: z
          .string()
          .trim()
          .min(1, { message: t("contactForm.errors.messageRequired") })
          .max(1000, { message: t("contactForm.errors.messageMax") }),
      }),
    [t]
  );

  type ContactFormValues = z.infer<typeof contactSchema>;

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      phone: "",
      topic: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormValues) => {
    setSubmitting(true);
    const language = i18n.language?.startsWith("sv") ? "sv" : "en";
    const id = crypto.randomUUID();

    try {
      const { error: insertError } = await supabase
        .from("contact_submissions")
        .insert({
          id,
          name: data.name,
          email: data.email,
          company: data.company || null,
          phone: data.phone || null,
          topic: data.topic,
          message: data.message,
          language,
          user_agent: navigator.userAgent.slice(0, 512),
          page_url: window.location.href.slice(0, 2048),
        });

      if (insertError) throw insertError;

      trackEvent("generate_lead", {
        form_location: "contact_page",
        topic: data.topic,
        has_company: Boolean(data.company),
        has_phone: Boolean(data.phone),
      });

      // Emails are composed server-side from the stored submission; failures
      // don't break the submission since the data is already saved.
      const { error: emailError } = await supabase.functions.invoke("send-contact-emails", {
        body: { submissionId: id },
      });
      if (emailError) console.error("Contact emails failed", emailError);


      toast.success(t("contactForm.toastSuccess", { email: data.email }));
      setSubmittedEmail(data.email);
      form.reset();
    } catch (err) {
      console.error("Contact form submission failed", err);
      toast.error(t("contactForm.toastError"));
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedEmail) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <div className="mx-auto mb-6 inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <CheckCircle2 className="h-8 w-8 text-primary" aria-hidden="true" />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold mb-3">
          {t("contactForm.success.title")}
        </h2>
        <p className="text-muted-foreground mb-6">
          {t("contactForm.success.body", { email: submittedEmail })}
        </p>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setSubmittedEmail(null)}
        >
          {t("contactForm.success.sendAnother")}
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-8">
      <h2 className="text-2xl md:text-3xl font-bold mb-6">{t("contactForm.heading")}</h2>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("contactForm.fields.name.label")} *</FormLabel>
                <FormControl>
                  <Input placeholder={t("contactForm.fields.name.placeholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("contactForm.fields.email.label")} *</FormLabel>
                <FormControl>
                  <Input type="email" placeholder={t("contactForm.fields.email.placeholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("contactForm.fields.company.label")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("contactForm.fields.company.placeholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("contactForm.fields.phone.label")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("contactForm.fields.phone.placeholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="topic"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("contactForm.fields.topic.label")} *</FormLabel>
                <FormControl>
                  <Input placeholder={t("contactForm.fields.topic.placeholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("contactForm.fields.message.label")} *</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t("contactForm.fields.message.placeholder")}
                    className="min-h-[150px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" variant="primary" size="lg" className="w-full" disabled={submitting}>
            {submitting ? t("contactForm.submitting") : t("contactForm.submit")}
          </Button>

          <p className="text-sm text-muted-foreground text-center mt-4">
            <Trans
              i18nKey="contactForm.privacyNotice"
              components={{
                1: (
                  <Link
                    to={localize("/privacy-policy")}
                    className="underline hover:text-primary transition-colors"
                  />
                ),
              }}
            />
          </p>
        </form>
      </Form>
    </div>
  );
};

export default ContactForm;
